import React from "react";
import ReactDOM from "react-dom/client";
import {
  QueryClient,
  QueryFunctionContext,
  QueryClientProvider,
} from "react-query";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { api } from "@app/api";
import reportWebVitals from "@app/reportWebVitals";
import router from "@app/routes/routes";
import { store } from "@app/store";
import "@app/styles/scss/styles.scss";
import i18n from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";
import intervalPlural from "i18next-intervalplural-postprocessor";
import translationEn from "@app/locale/en.json";
import translationJa from "@app/locale/ja.json";
import translationPt from "@app/locale/pt.json";
import { RTL_LANGUAGES, SelectLanguage } from "@app/constants";
import translationEs from "@app/locale/es.json";
import translationAr from "@app/locale/ar.json";
import translationUr from "@app/locale/ur.json";
import translationTh from "@app/locale/th.json";
import translationFr from "@app/locale/fr.json";
import translationVi from "@app/locale/vi.json";
import translationLo from "@app/locale/lo.json";
import translationKm from "@app/locale/km.json";
import translationRu from "@app/locale/ru.json";
import { MuiDirectionProvider } from "@app/components/providers/MuiDirectionProvider";
import { createAppTheme } from "@app/theme/muiTheme";

const defaultQueryFn = async ({ queryKey }: QueryFunctionContext<any>) => {
  const { data } = await api.get(queryKey[0]);
  return data;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
    },
  },
});
const i18nInstance = i18n.createInstance();

const resources = {
  en: { translation: translationEn },
  ja: { translation: translationJa },
  es: { translation: translationEs },
  pt: { translation: translationPt },
  ar: { translation: translationAr },
  ur: { translation: translationUr },
  th: { translation: translationTh },
  fr: { translation: translationFr },
  vi: { translation: translationVi },
  lo: { translation: translationLo },
  km: { translation: translationKm },
  ru: { translation: translationRu },
};

const availableLanguages = Object.values(SelectLanguage);

const browserLang = navigator.language.split("-")[0].toLowerCase();

const currentLanguage = availableLanguages.includes(
  browserLang as SelectLanguage,
)
  ? (browserLang as SelectLanguage)
  : SelectLanguage.EN;

i18nInstance
  .use(initReactI18next)
  .use(intervalPlural)
  .init({
    resources,
    lng: currentLanguage,
    fallbackLng: SelectLanguage.EN,
    simplifyPluralSuffix: true,
    interpolation: {
      escapeValue: false,
    },
  });

document.documentElement.dir = RTL_LANGUAGES.includes(currentLanguage)
  ? "rtl"
  : "ltr";
document.documentElement.lang = currentLanguage;

/** @deprecated Use createAppTheme(direction) — theme direction follows active language via MuiDirectionProvider */
export const theme = createAppTheme(
  RTL_LANGUAGES.includes(currentLanguage) ? "rtl" : "ltr",
);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18nInstance} defaultNS={"translation"}>
          <MuiDirectionProvider>
            <RouterProvider router={router} />
          </MuiDirectionProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>,
);

reportWebVitals();
